/**
 * ╔══════════════════════════════════════════════════════════════╗
 * ║  useClientData — Dynamische Kundendaten aus Auth-Context     ║
 * ║                                                              ║
 * ║  Ersetzt die statische clientConfig für den /app-Bereich.    ║
 * ║  Lädt Anamnese aus der DB und baut die                       ║
 * ║  System-Prompts on-the-fly über den Prompt-Builder.          ║
 * ╚══════════════════════════════════════════════════════════════╝
 */

import { useMemo } from "react";
import { useClientAuth, type ClientUser } from "@/contexts/ClientAuthContext";
import {
  buildTagebuchPrompt,
  buildAyuCoachPrompt,
} from "@shared/promptBuilder";

export interface ClientData {
  /** Vorname */
  firstName: string;
  /** Nachname */
  lastName: string;
  /** Client-ID (für DB-Queries) */
  clientId: string;
  /** Paket-Typ */
  paket: "basis" | "premium";
  /** Ob Anamnese vorhanden */
  hasAnamnese: boolean;
  /** Tagebuch System-Prompt (automatisch gebaut) */
  tagebuchPrompt: string;
  /** AYU Coach System-Prompt (automatisch gebaut) */
  ayuCoachPrompt: string;
  /** Coaching-Stil (falls gespeichert) */
  coachingStyle: string | null;
  /** Begrüßung */
  greeting: string;
  /** Betreuer-Name */
  betreuerName: string;
  /** Betreuer-Foto */
  betreuerFoto: string;
  /** Startdatum */
  startDate: string;
  /** Ablaufdatum */
  appExpiryDate: string;
  /** Stimmen-Präferenz (annam/christopher) */
  voicePreference: "annam" | "christopher";
  /** Rohe Anamnese-Daten (für Horoskop etc.) */
  anamneseData: unknown;
  /** Ob Daten geladen werden */
  isLoading: boolean;
  /** Ob eingeloggt */
  isAuthenticated: boolean;
  /** Ob Admin-Zugriff */
  isAdmin: boolean;
}

/**
 * Hook der die Kundendaten dynamisch aus dem Auth-Context lädt
 * und die System-Prompts on-the-fly baut.
 *
 * Unterstützt sowohl AnamneseData als auch SurveyResponse als
 * gespeichertes Datenformat — der Prompt-Builder erkennt das
 * Format automatisch.
 */
export function useClientData(): ClientData {
  const { client, loading: isLoading, isAuthenticated } = useClientAuth();

  const data = useMemo<ClientData>(() => {
    if (!client) {
      return {
        firstName: "",
        lastName: "",
        clientId: "",
        paket: "basis",
        hasAnamnese: false,
        tagebuchPrompt: "",
        ayuCoachPrompt: "",
        coachingStyle: null,
        greeting: "Willkommen",
        betreuerName: "Lilli",
        betreuerFoto:
          "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/WZX5AU3w4QaCsGPxruXuzA/lilli-betreuerin_4c2c5d0e.jpeg",
        startDate: new Date().toISOString().split("T")[0],
        appExpiryDate: "",
        voicePreference: "annam",
        anamneseData: null,
        isLoading,
        isAuthenticated,
        isAdmin: false,
      };
    }

    // Anamnese-Daten aus DB (kann AnamneseData ODER SurveyResponse sein)
    // WICHTIG: anamneseData enthält die Fragebogen-Antworten, doshaProfile nur das Phänotyp-Ergebnis
    const rawAnamnese = client.onboardingComplete
      ? (client.anamneseData ?? client.doshaProfile ?? null)
      : null;

    // Prompts on-the-fly bauen — der Prompt-Builder erkennt das Format automatisch
    // und nutzt den clientName als Fallback wenn die Daten im SurveyResponse-Format sind
    const tagebuchPrompt = buildTagebuchPrompt(
      rawAnamnese ?? {},
      null,
      client.firstName,
      client.doshaProfile,
      client.voicePreference
    );
    const ayuCoachPrompt = buildAyuCoachPrompt(
      rawAnamnese ?? {},
      null,
      client.firstName,
      client.doshaProfile,
      client.voicePreference
    );

    return {
      firstName: client.firstName,
      lastName: client.lastName,
      clientId: client.clientId,
      paket: client.packageType,
      hasAnamnese: client.onboardingComplete,
      tagebuchPrompt,
      ayuCoachPrompt,
      coachingStyle: client.coachingStyle,
      greeting: `Schön, dass du da bist, ${client.firstName}.`,
      betreuerName: "Lilli",
      betreuerFoto:
        "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/WZX5AU3w4QaCsGPxruXuzA/lilli-betreuerin_4c2c5d0e.jpeg",
      startDate: new Date().toISOString().split("T")[0],
      appExpiryDate: "",
      voicePreference: client.voicePreference ?? "annam",
      anamneseData: rawAnamnese,
      isLoading,
      isAuthenticated,
      isAdmin: (client as any).isAdmin ?? false,
    };
  }, [client, isLoading, isAuthenticated]);

  return data;
}
